import React from 'react';
import {
  Draggable as RBDraggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from '@hello-pangea/dnd';

import colors from '@atom/styles/colors';

const getDefaultItemStyle = (
  isDragStyleDisabled: boolean,
  isDragging: boolean,
): React.CSSProperties => ({
  background:
    isDragStyleDisabled || !isDragging
      ? colors.neutral.white
      : colors.neutral.fog,
  boxShadow:
    isDragStyleDisabled || !isDragging
      ? 'none'
      : 'rgba(0, 0, 0, 0.12) 0 1px 6px, rgba(0, 0, 0, 0.12) 0 1px 4px',
});

type DraggableChildren =
  | React.ReactNode
  | ((state: { isDragging: boolean }) => React.ReactNode);

interface Props {
  index: number;
  draggableId: string;
  isDragDisabled?: boolean;
  isDragStyleDisabled?: boolean;
  className?: string;
  /**
   * When provided, replaces the default Atom drag style. The result is
   * merged with hello-pangea's draggable transform/transition styles.
   */
  style?: (isDragging: boolean) => React.CSSProperties;
  children: DraggableChildren;
}

export const Draggable = ({
  index,
  draggableId,
  isDragDisabled = false,
  isDragStyleDisabled = false,
  className,
  style,
  children,
}: Props) => {
  return (
    <RBDraggable
      draggableId={draggableId}
      index={index}
      isDragDisabled={isDragDisabled}
    >
      {(
        draggableProvided: DraggableProvided,
        draggableSnapshot: DraggableStateSnapshot,
      ) => {
        const { isDragging } = draggableSnapshot;
        const consumerStyle = style
          ? style(isDragging)
          : getDefaultItemStyle(isDragStyleDisabled, isDragging);
        return (
          <div
            ref={draggableProvided.innerRef}
            className={className}
            {...draggableProvided.draggableProps}
            {...draggableProvided.dragHandleProps}
            style={{
              userSelect: 'none',
              ...consumerStyle,
              ...draggableProvided.draggableProps.style,
            }}
          >
            {typeof children === 'function'
              ? children({ isDragging })
              : children}
          </div>
        );
      }}
    </RBDraggable>
  );
};
